import { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import { exportSheet, todayStamp } from '../excel';
import KPICard from '../components/KPICard';
import DataFreshness from '../components/DataFreshness';
import DataTable from '../components/DataTable';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import { ResponsiveBar } from '@nivo/bar';
import { useSwrStore, useSwrQuery } from '../swrCache';

const PURPLE_600 = '#6E58A6';
const TEXT_MUTED = '#736A85';
const CHANNEL_COLORS = ['#6E58A6', '#9A7BC4', '#3A6B8A', '#5C7E60', '#9A7B3E', '#C25E77', '#4A5A8A', '#7E5C8A'];

const MONTH_OPTIONS = [
    { value: 9,  label: 'Sep 2025' },
    { value: 10, label: 'Oct 2025' },
    { value: 11, label: 'Nov 2025' },
    { value: 12, label: 'Dec 2025' },
];
const FESTIVE_MIN_DATE = '2025-09-01';
const FESTIVE_MAX_DATE = '2025-12-31';

function fmtMoney(n) {
    if (n === null || n === undefined) return '—';
    if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr';
    if (n >= 100000)   return '₹' + (n / 100000).toFixed(1) + ' L';
    return '₹' + Number(n).toLocaleString('en-IN');
}

function growthBadge(n) {
    if (n === null || n === undefined) return null;
    return { text: (n > 0 ? '+' : '') + n + '%', positive: n > 0 ? true : n < 0 ? false : null };
}

function ChartCard({ title, sub, height = 260, children }) {
    return (
        <div style={{ minWidth: 0, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '18px 20px' }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0F1F2E' }}>{title}</h3>
            {sub && <p style={{ margin: '2px 0 12px', fontSize: 12, color: '#8C82A0' }}>{sub}</p>}
            <div style={{ height }}>{children}</div>
        </div>
    );
}

const barTheme = {
    grid: { line: { stroke: '#F1F5F9', strokeDasharray: '3 3' } },
    axis: { ticks: { text: { fontSize: 11.5, fill: TEXT_MUTED } } },
};

export default function FestiveSeasonTrends() {
    const [filterOptions, setFilterOptions] = useState({ platforms: [], skus: [], cities: [] });
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [selectedSkus, setSelectedSkus] = useState([]);
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        apiFetch('festive-trends-filters').then(setFilterOptions).catch(console.error);
    }, []);

    const store = useSwrStore();
    const cacheKey = [
        `platforms=${selectedPlatforms.join(',')}`,
        `skus=${selectedSkus.join(',')}`,
        `months=${selectedMonths.join(',')}`,
        `cities=${selectedCities.join(',')}`,
        `from=${dateFrom}`, `to=${dateTo}`,
    ].join('&');

    const { data, loading, error: fetchError, fetchedAt, refresh } = useSwrQuery(store, cacheKey, async () => {
        const params = {};
        if (selectedPlatforms.length) params.platforms = selectedPlatforms.join(',');
        if (selectedSkus.length) params.skus = selectedSkus.join(',');
        if (selectedMonths.length) params.months = selectedMonths.join(',');
        if (selectedCities.length) params.cities = selectedCities.join(',');
        if (dateFrom) params.from = dateFrom;
        if (dateTo) params.to = dateTo;
        return apiFetch('festive-trends', params);
    });
    const error = fetchError ? 'Could not load festive trends — the database may be slow right now. Try again in a moment.' : '';
    const showLoadingOverlay = loading && !data;

    const platformOptions = filterOptions.platforms.map(p => ({ value: p, label: p }));
    const skuOptions = filterOptions.skus.map(s => ({ value: s.name, label: s.name }));
    const cityOptions = filterOptions.cities.map(c => ({ value: c, label: c }));

    function handleExport() {
        if (!data || !data.table.length) return;
        const filterBits = [];
        if (selectedPlatforms.length) filterBits.push(selectedPlatforms.join('+'));
        if (selectedMonths.length) filterBits.push(MONTH_OPTIONS.filter(m => selectedMonths.includes(m.value)).map(m => m.label.replace(' ', '')).join('+'));
        const suffix = filterBits.length ? `_${filterBits.join('_').replace(/[^A-Za-z0-9+_-]/g, '')}` : '';
        exportSheet({
            headers: ['EAN Code', 'SKU Name', 'Qty Sold'],
            rows: data.table.map(r => [r.ean_code || '', r.sku_name || '', r.qty]),
            sheetName: 'Festive Trends',
            filename: `festive_season_trends${suffix}_${todayStamp()}.xlsx`,
        });
    }

    const monthChartData = (data?.month_trend || []).map(m => ({ label: m.label, Units: m.qty, 'Units LY': m.qty_ly }));
    const platformChartData = [...(data?.platform_breakdown || [])].reverse();
    const categoryChartData = [...(data?.category_breakdown || [])].reverse();

    return (
        <div className="sales-overview-page">
            <div style={{
                display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
                background: PURPLE_600, borderRadius: 16, boxShadow: '0 6px 20px rgba(35,28,48,0.10)',
                padding: '14px 20px', marginBottom: 18,
            }}>
                <div>
                    <h1 style={{ fontWeight: 700, fontSize: 22, margin: 0, color: '#fff', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>Festive Season Trends</h1>
                    <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#E4DCF0' }}>Track the Festive season trends of Soulflower — Sep to Dec 2025, all platforms</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
                    <DataFreshness fetchedAt={fetchedAt} loading={loading} onRefresh={refresh} style={{ color: '#E4DCF0' }} />
                </div>
            </div>

            <div data-tutorial="festive-filters" style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                padding: '14px 18px', marginBottom: 18,
            }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#8C82A0', textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4 }}>Filters</span>
                {platformOptions.length > 0 && (
                    <MultiSelectDropdown values={selectedPlatforms} options={platformOptions} onChange={setSelectedPlatforms} placeholder="Platforms" />
                )}
                {skuOptions.length > 0 && (
                    <MultiSelectDropdown values={selectedSkus} options={skuOptions} onChange={setSelectedSkus} placeholder="SKUs" />
                )}
                <MultiSelectDropdown values={selectedMonths} options={MONTH_OPTIONS} onChange={setSelectedMonths} placeholder="Months" />
                {cityOptions.length > 0 && (
                    <MultiSelectDropdown values={selectedCities} options={cityOptions} onChange={setSelectedCities} placeholder="Locations" />
                )}
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#3D3452' }}>
                    <input type="date" value={dateFrom} min={FESTIVE_MIN_DATE} max={dateTo || FESTIVE_MAX_DATE}
                        onChange={e => setDateFrom(e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #E4DCF0', fontSize: 12.5 }} />
                    <span style={{ color: '#8C82A0' }}>to</span>
                    <input type="date" value={dateTo} min={dateFrom || FESTIVE_MIN_DATE} max={FESTIVE_MAX_DATE}
                        onChange={e => setDateTo(e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #E4DCF0', fontSize: 12.5 }} />
                </span>
                {Boolean(selectedPlatforms.length || selectedSkus.length || selectedMonths.length || selectedCities.length || dateFrom || dateTo) && (
                    <button
                        type="button"
                        onClick={() => { setSelectedPlatforms([]); setSelectedSkus([]); setSelectedMonths([]); setSelectedCities([]); setDateFrom(''); setDateTo(''); }}
                        style={{ padding: '7px 12px', borderRadius: 999, border: 'none', background: '#F1EDF9', color: '#6E58A6', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                    >
                        Clear filters
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={!data || !data.table.length}
                    style={{
                        marginLeft: 'auto', padding: '8px 16px', borderRadius: 999, border: 'none',
                        background: (!data || !data.table.length) ? '#D9D2E8' : PURPLE_600, color: '#fff', fontSize: 12.5, fontWeight: 700,
                        cursor: (!data || !data.table.length) ? 'not-allowed' : 'pointer',
                    }}
                >
                    ⬇ Download Excel
                </button>
            </div>

            <div style={{ position: 'relative' }}>
                {showLoadingOverlay && <p className="loading">Loading festive trends...</p>}
                {error ? <p className="empty" style={{ color: '#EF4444' }}>{error}</p> : !data ? null : (
                    <>
                        <div className="kpi-row" style={{ marginBottom: 20 }}>
                            <KPICard label="Units Sold" value={data.kpis.total_units.toLocaleString('en-IN')} badge={growthBadge(data.kpis.yoy_units_growth)} sub="vs same period 2024" />
                            <KPICard label="Revenue" value={fmtMoney(data.kpis.total_revenue)} sub="selected filters" />
                            <KPICard label="Active SKUs" value={data.kpis.active_skus.toLocaleString('en-IN')} sub="sold in period" />
                            <KPICard label="Best Month" value={data.kpis.best_month || '—'} sub={data.kpis.best_month_qty ? `${data.kpis.best_month_qty.toLocaleString('en-IN')} units` : ''} />
                            <KPICard label="Best Platform" value={data.kpis.best_platform || '—'} sub={data.kpis.best_platform_qty ? `${data.kpis.best_platform_qty.toLocaleString('en-IN')} units` : ''} />
                            <KPICard label="Best City" value={data.kpis.best_city || '—'} sub={data.kpis.best_city_qty ? `${data.kpis.best_city_qty.toLocaleString('en-IN')} units` : ''} />
                        </div>

                        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '18px 20px', marginBottom: 20 }}>
                            <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#0F1F2E' }}>Units Sold — SKU wise</h3>
                            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#8C82A0' }}>{data.table.length.toLocaleString('en-IN')} SKUs for the selected filters</p>
                            <DataTable
                                data={data.table}
                                columns={[
                                    { key: 'ean_code', label: 'EAN Code' },
                                    { key: 'sku_name', label: 'SKU Name' },
                                    { key: 'qty', label: 'Qty Sold' },
                                ]}
                            />
                        </div>

                        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0F1F2E', margin: '4px 0 14px' }}>Festive Season Analytics</h2>

                        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, marginBottom: 16 }}>
                            <ChartCard title="Month-wise Sales Trend" sub="Units sold, 2025 vs 2024 (same months)">
                                {monthChartData.length === 0 ? <p className="empty">No data</p> : (
                                    <ResponsiveBar
                                        data={monthChartData}
                                        keys={['Units', 'Units LY']}
                                        indexBy="label"
                                        groupMode="grouped"
                                        margin={{ top: 10, right: 10, bottom: 30, left: 55 }}
                                        padding={0.3}
                                        colors={[PURPLE_600, '#C4B4E4']}
                                        borderRadius={4}
                                        enableGridX={false}
                                        theme={barTheme}
                                        axisLeft={{ tickValues: 4 }}
                                        legends={[{ dataFrom: 'keys', anchor: 'top-right', direction: 'row', translateY: -18, itemWidth: 60, itemHeight: 18, symbolSize: 9, symbolShape: 'circle', itemTextColor: '#475569' }]}
                                        animate
                                        motionConfig="gentle"
                                    />
                                )}
                            </ChartCard>

                            <ChartCard title="Platform-wise Split" sub="Revenue by channel">
                                {platformChartData.length === 0 ? <p className="empty">No data</p> : (
                                    <ResponsiveBar
                                        data={platformChartData}
                                        keys={['revenue']}
                                        indexBy="platform"
                                        layout="horizontal"
                                        margin={{ top: 6, right: 40, bottom: 24, left: 90 }}
                                        padding={0.35}
                                        colors={({ indexValue }) => CHANNEL_COLORS[platformChartData.findIndex(p => p.platform === indexValue) % CHANNEL_COLORS.length]}
                                        borderRadius={4}
                                        enableGridY={false}
                                        enableLabel={false}
                                        axisBottom={{ format: v => fmtMoney(v), tickValues: 3 }}
                                        axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                        theme={barTheme}
                                        animate
                                        motionConfig="gentle"
                                        tooltip={({ indexValue, value, data: d }) => (
                                            <div style={{ background: '#fff', padding: '8px 12px', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 12.5 }}>
                                                <div style={{ fontWeight: 700, marginBottom: 2 }}>{indexValue}</div>
                                                <div>{fmtMoney(value)} · {d.qty.toLocaleString('en-IN')} units</div>
                                            </div>
                                        )}
                                    />
                                )}
                            </ChartCard>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <ChartCard title="Category-wise Split" sub="Revenue by category" height={Math.max(200, categoryChartData.length * 36)}>
                                {categoryChartData.length === 0 ? <p className="empty">No data</p> : (
                                    <ResponsiveBar
                                        data={categoryChartData}
                                        keys={['revenue']}
                                        indexBy="category"
                                        layout="horizontal"
                                        margin={{ top: 6, right: 40, bottom: 24, left: 110 }}
                                        padding={0.35}
                                        colors={({ indexValue }) => CHANNEL_COLORS[categoryChartData.findIndex(c => c.category === indexValue) % CHANNEL_COLORS.length]}
                                        borderRadius={4}
                                        enableGridY={false}
                                        enableLabel={false}
                                        axisBottom={{ format: v => fmtMoney(v), tickValues: 3 }}
                                        axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                        theme={barTheme}
                                        animate
                                        motionConfig="gentle"
                                    />
                                )}
                            </ChartCard>

                            <ChartCard title="Top Cities" sub="By units sold" height="auto">
                                {!data.city_breakdown.length ? <p className="empty">No data</p> : (() => {
                                    const maxQty = data.city_breakdown[0].qty || 1;
                                    return data.city_breakdown.map((c, i) => (
                                        <div key={c.city} style={{ marginBottom: 12 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                                                <span style={{ fontWeight: 600, color: '#1E293B' }}>{c.city}</span>
                                                <span style={{ color: '#8C82A0' }}>{c.qty.toLocaleString('en-IN')} units</span>
                                            </div>
                                            <div style={{ height: 6, borderRadius: 999, background: '#F1EDF9', overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${(c.qty / maxQty) * 100}%`, borderRadius: 999, background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </ChartCard>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
